import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { Organization } from '../types/user';

interface OrganizationThemeContextType {
  organization: Organization | null;
}

const OrganizationThemeContext = createContext<OrganizationThemeContextType>({
  organization: null,
});

export const useOrganizationTheme = () => {
  return useContext(OrganizationThemeContext);
};

interface OrganizationThemeProviderProps {
  organization: Organization | null;
  children: React.ReactNode;
}

export const OrganizationThemeProvider: React.FC<OrganizationThemeProviderProps> = ({
  organization,
  children,
}) => {
  // Get the current theme from the parent ThemeContext
  const baseTheme = useTheme();

  const customTheme = useMemo(() => {

    // If no organization or no custom branding, use base theme
    if (!organization?.branding?.primary_color && !organization?.branding?.secondary_color) {

      return baseTheme;
    }

    // Create custom theme with organization colors
    const primaryColor = organization.branding.primary_color || baseTheme.palette.primary.main;
    const secondaryColor = organization.branding.secondary_color || baseTheme.palette.secondary.main;
    const mode = baseTheme.palette.mode;

    // Helper function to lighten color
    const lightenColor = (color: string, amount: number = 40): string => {
      const hex = color.replace('#', '');
      const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + amount);
      const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + amount);
      const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + amount);
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Helper function to darken color
    const darkenColor = (color: string, amount: number = 40): string => {
      const hex = color.replace('#', '');
      const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - amount);
      const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - amount);
      const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - amount);
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const newTheme = createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          main: primaryColor,
          light: lightenColor(primaryColor),
          dark: darkenColor(primaryColor),
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: secondaryColor,
          light: lightenColor(secondaryColor),
          dark: darkenColor(secondaryColor),
          contrastText: '#FFFFFF',
        },
      },
      components: {
        ...baseTheme.components,
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              padding: '10px 24px',
              fontSize: '1rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${primaryColor}4D`, // 30% opacity
              },
            },
            contained: {
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              boxShadow: `0 4px 16px ${primaryColor}4D`,
              '&:hover': {
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                boxShadow: `0 6px 24px ${primaryColor}66`, // 40% opacity
              },
            },
            outlined: {
              borderWidth: 2,
              borderColor: primaryColor,
              color: primaryColor,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: `${primaryColor}14`, // 8% opacity
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              // Don't override background - let base theme handle it
              borderRadius: 16,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              fontWeight: 500,
              // Let base theme handle background, only customize border
              border: `1px solid ${primaryColor}4D`, // 30% opacity
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              // Let base theme handle background colors
              '& .MuiOutlinedInput-root': {
                '&:hover': {
                  '& fieldset': {
                    borderColor: `${primaryColor}80`, // 50% opacity
                  },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: primaryColor,
                    borderWidth: 2,
                  },
                },
              },
            },
          },
        },
      },
    });

    return newTheme;
  }, [organization?.branding?.primary_color, organization?.branding?.secondary_color, baseTheme]);

  const value = {
    organization,
  };

  return (
    <OrganizationThemeContext.Provider value={value}>
      <ThemeProvider theme={customTheme}>{children}</ThemeProvider>
    </OrganizationThemeContext.Provider>
  );
};
